import { NavLink } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext.tsx'
import { useOffline } from '../offline/OfflineContext.tsx'
import { navFor, roleLabel } from '../nav.ts'
import { plural } from '../format.ts'
import { useState, type ReactNode } from 'react'
import { ChangePasswordDialog } from './ChangePasswordDialog.tsx'

/**
 * The frame around every logged-in screen: a sidebar whose contents depend on
 * the user's role, and a status bar showing connectivity (SPEC 5.7).
 */
export function AppShell({ children }: { children: ReactNode }) {
  const { user, logout } = useAuth()
  const [zmianaHasla, setZmianaHasla] = useState(false)
  const { online, queueCount } = useOffline()
  if (!user) return null

  const items = navFor(user)

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <div className="flex flex-1 min-h-0">
        {/* Sidebar */}
        <aside className="w-56 shrink-0 bg-brand-900 text-brand-50 flex flex-col">
          <div className="px-4 py-4 border-b border-brand-700/50">
            <div className="font-bold leading-tight">Centrum Sadownicze</div>
            <div className="text-brand-100/70 text-sm">Warka — CRM</div>
          </div>

          <nav className="flex-1 py-2 overflow-y-auto">
            {items.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === '/'}
                className={({ isActive }) =>
                  `block px-4 py-2 text-sm transition-colors ${
                    isActive ? 'bg-brand-700 text-white font-medium' : 'text-brand-100/90 hover:bg-brand-700/50'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="px-4 py-3 border-t border-brand-700/50 text-sm">
            <div className="font-medium">
              {user.firstName} {user.lastName}
            </div>
            <div className="text-brand-100/70 text-xs mb-2">
              {roleLabel(user.role)} · {user.code}
            </div>
            <div className="flex gap-3">
              <button onClick={() => setZmianaHasla(true)} className="text-brand-100/80 hover:text-white text-xs underline">
                Zmień hasło
              </button>
              <button onClick={() => void logout()} className="text-brand-100/80 hover:text-white text-xs underline">
                Wyloguj
              </button>
            </div>
            {zmianaHasla && <ChangePasswordDialog onClose={() => setZmianaHasla(false)} />}
          </div>
        </aside>

        {/* Content */}
        <main className="flex-1 min-w-0 overflow-auto">{children}</main>
      </div>

      {/* Status bar (SPEC 5.7) */}
      <footer className="shrink-0 bg-white border-t border-slate-200 px-4 py-1.5 text-xs text-slate-500 flex items-center gap-2">
        <span className={online ? 'text-green-600' : 'text-amber-600'}>●</span>
        <span>
          {online ? 'Online' : 'Offline'}
          {queueCount > 0 && (
            <span className="text-amber-700">
              {' — '}
              {queueCount} {plural(queueCount, 'szkic czeka', 'szkice czekają', 'szkiców czeka')} na wysłanie
            </span>
          )}
        </span>
        {/* A shortcut nobody is told about is a shortcut nobody uses. */}
        <span className="ml-auto text-slate-400">
          <kbd className="font-mono bg-slate-100 border border-slate-200 rounded px-1">Ctrl</kbd>
          {' + '}
          <kbd className="font-mono bg-slate-100 border border-slate-200 rounded px-1">K</kbd> — szukaj wszędzie
        </span>
      </footer>
    </div>
  )
}
